import React, { useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import { shuffle } from 'lodash';
import { useTranslations } from 'next-intl';

import 'swiper/css';

import Headline from '@components/atoms/headline/headline';
import Image from '@components/atoms/image/image';
import ArrowLink from '@components/molecules/arrow-link/arrowLink';

import StyledStakeholder from './stakeholder.style';

const Stakeholder = ({ hasBackground = false, logos, link = null }) => {
  const t = useTranslations();
  const sliderRef = useRef();
  const [isPlaying, setIsPlaying] = useState(true);
  const [locked, setLocked] = useState(false);
  const updatedLogos = useMemo(() => [...shuffle(logos), ...shuffle(logos)], []);

  const handlePlaying = () => {
    if (!sliderRef.current) return;
    const { swiper } = sliderRef.current;

    if (swiper.autoplay.running) {
      const sliderTranslate = swiper.getTranslate();
      swiper.setTranslate(sliderTranslate);
      setIsPlaying(false);
      swiper.autoplay.stop();
    } else {
      swiper.slideTo(swiper.activeIndex, 2000, false);
      setIsPlaying(true);
      swiper.autoplay.start();
    }
  };

  return (
    <StyledStakeholder
      $hasBackground={hasBackground}
      $isLocked={locked}
      aria-label={t('stakeholder.headline')}
    >
      <Headline tag="h2">{t('stakeholder.headline')}</Headline>

      <Swiper
        ref={sliderRef}
        autoplay={{
          delay: 1
        }}
        speed={6000}
        spaceBetween={0}
        slidesPerView="auto"
        loop
        freeMode
        allowTouchMove={false}
        modules={[Autoplay]}
        onResize={swiper => {
          setLocked(swiper.isLocked);
        }}
        breakpoints={{
          768: {
            spaceBetween: 50
          }
        }}
      >
        {updatedLogos?.map((logo, index) => (
          <SwiperSlide
            key={index}
            style={{
              '--aspectRatio': `${logo.width}/${logo.height}`
            }}
          >
            <Image {...logo} />
          </SwiperSlide>
        ))}
      </Swiper>
      <div className="stakeholder__meta">
        <div className="stakeholder__controls">
          <button
            type="button"
            disabled={locked}
            aria-label={t(isPlaying ? 'pause' : 'play')}
            onClick={handlePlaying}
            className="stakeholder__button"
          >
            <div className="stakeholder__button-icon">
              {isPlaying ? (
                <svg
                  aria-hidden="true"
                  width="21"
                  height="21"
                  viewBox="0 0 21 21"
                  fill="none"
                  focusable="false"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect x="5" y="4" width="2.80253" height="13.0121" fill="currentColor" />
                  <rect x="13" y="4" width="2.80253" height="13.0121" fill="currentColor" />
                </svg>
              ) : (
                <svg
                  aria-hidden="true"
                  width="21"
                  height="21"
                  viewBox="0 0 21 21"
                  fill="none"
                  focusable="false"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M16 10.5L6 16V5L16 10.5Z" fill="currentColor" />
                </svg>
              )}
            </div>
            <span className="stakeholder__button-label">
              {t(isPlaying ? 'animationPause' : 'animationPlay')}
            </span>
          </button>
        </div>
        {link && <ArrowLink href={link.url} title={link.title} size="50" />}
      </div>
    </StyledStakeholder>
  );
};

Stakeholder.propTypes = {
  hasBackground: PropTypes.bool,
  logos: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string,
      srcset: PropTypes.string,
      alt: PropTypes.string
    })
  ).isRequired,
  link: PropTypes.shape({
    title: PropTypes.string,
    url: PropTypes.string
  })
};

export default Stakeholder;
